import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Matrix4, Vector3 } from 'three';
import type { Group, PerspectiveCamera } from 'three';
import type { RefObject } from 'react';
import {
  DEFAULT_VEHICLE_SURFACE,
  createVehicleSpec,
  createVehicleState,
  stepVehicle,
  type VehicleInput,
  type VehicleState,
  type VehicleSurfaceCondition,
} from '../model/vehicleDriving';
import {
  sampleDrivingTerrain,
  type DrivingTerrainSample,
} from '../model/drivingTerrain';
import {
  applyDrivingCameraShake,
  beginDrivingCameraShake,
  createDrivingCameraPose,
  createDrivingCameraShakeState,
  resetDrivingCameraShake,
  restoreDrivingCameraBase,
  snapDrivingCamera,
  updateDrivingCamera,
} from './drivingCamera';
import { stepDrivingSession, type DrivingSessionState } from '../model/drivingSession';
import {
  stepDrivingChallenge,
  type DrivingChallengeState,
} from '../model/drivingChallenges';
import {
  stepDrivingTelemetry,
  type DrivingTelemetryState,
} from '../model/drivingTelemetry';
import type { DrivingCourseOption } from '../data/courseOptions';
import type { DrivingCameraOption } from '../data/drivingCameraOptions';
import type { DrivingChallengeOption, DrivingChallengeId } from '../data/challengeOptions';
import type { DrivingAssists, RegenMode } from '../model/drivingPreferences';
import type { VehicleTuning } from '../model/vehicleTuning';
import type { Vector3Tuple } from '../types';
import type { CarBounds, ElectricEnergySpec, Powertrain } from '../types';

export interface CarDriverProps {
  vehicleId: string;
  bounds: CarBounds;
  /** 선택한 차량의 구동 방식. 변속 표시와 계기판 종류를 정합니다. */
  powertrain: Powertrain;
  /** 선택한 차량의 제조사 공개 최고속도 (km/h). */
  topSpeedKmh: number;
  /** 선택한 차량의 제조사 공개 0→100km/h 시간 (초). */
  zeroTo100KmhSeconds: number;
  /** 선택한 차량의 공차중량 (kg). 계기판 출력(kW)에 씁니다. */
  kerbWeightKg: number;
  /** 순수 전기차의 배터리·효율·회생 제원입니다. */
  electricEnergy?: ElectricEnergySpec;
  /** 가속 페달을 놓았을 때 적용할 회생 감속 단계입니다. */
  regenMode: RegenMode;
  /** 현재 날씨가 정한 노면 그립과 구름 저항입니다. */
  surface?: VehicleSurfaceCondition;
  course: DrivingCourseOption;
  assists: DrivingAssists;
  tuning: VehicleTuning;
  camera: DrivingCameraOption;
  challenge: DrivingChallengeOption;
  /** 실내 눈 위치. 운전석 시점에 사용하며 없으면 추격으로 정상화됩니다. */
  interiorEye?: Vector3Tuple;
  sessionRef: RefObject<DrivingSessionState>;
  challengeRef: RefObject<DrivingChallengeState>;
  /** 10Hz 샘플과 최근 완료 그래프를 보관하는 ref입니다. */
  telemetryRef: RefObject<DrivingTelemetryState>;
  /** DOM 쪽 조작 UI가 매 입력마다 갱신합니다. */
  inputRef: RefObject<VehicleInput>;
  /** 운동 상태. 이 컴포넌트가 쓰고 HUD는 읽기만 합니다. */
  stateRef: RefObject<VehicleState>;
  /** 월드 위치·진행 방향·지형 경사를 받는 그룹. */
  carGroupRef: RefObject<Group | null>;
  /** 동적 롤·피치를 받는 그룹. 위 그룹의 자식이라 축이 차량 로컬 축입니다. */
  bodyTiltRef: RefObject<Group | null>;
  /** 랩이 끝난 프레임에 한 번만 호출합니다. */
  onLapCompleted?: (courseId: string, resultSeconds: number) => void;
  /** 챌린지가 complete로 전환된 프레임에 한 번만 호출합니다. */
  onChallengeCompleted?: (challengeId: DrivingChallengeId, result: number) => void;
}

interface TerrainPoseScratch {
  sample: DrivingTerrainSample;
  forward: Vector3;
  up: Vector3;
  right: Vector3;
  matrix: Matrix4;
}

function createTerrainPoseScratch(): TerrainPoseScratch {
  return {
    sample: { height: 0, slopeX: 0, slopeZ: 0 },
    forward: new Vector3(),
    up: new Vector3(),
    right: new Vector3(),
    matrix: new Matrix4(),
  };
}

/** 월드 yaw를 경사면에 투영해 차량의 +X 전면, +Y 위, +Z 오른쪽 기저를 만듭니다. */
function applyTerrainPose(group: Group, state: VehicleState, scratch: TerrainPoseScratch): void {
  const terrain = sampleDrivingTerrain(state.x, state.z, scratch.sample);
  const { forward, up, right, matrix } = scratch;

  up.set(-terrain.slopeX, 1, -terrain.slopeZ).normalize();
  const forwardX = Math.cos(state.heading);
  const forwardZ = -Math.sin(state.heading);
  /*
   * 운동 모델이 x/z를 이 방향으로 옮긴 뒤 높이장 y를 붙이므로, 실제 이동 접선의
   * y 성분은 dh/dx·dx + dh/dz·dz입니다. 수평 벡터를 법선에 직교 투영하면 대각선
   * 사면에서 x/z 방위까지 바뀌어 차가 진행 궤적과 다른 쪽을 보는 문제가 생깁니다.
   */
  forward.set(
    forwardX,
    terrain.slopeX * forwardX + terrain.slopeZ * forwardZ,
    forwardZ
  ).normalize();
  right.crossVectors(forward, up).normalize();
  // 수치 오차로 세 축이 비직교가 되지 않도록 up을 한 번 다시 만듭니다.
  up.crossVectors(right, forward).normalize();

  matrix.makeBasis(forward, up, right);
  group.position.set(state.x, terrain.height, state.z);
  group.quaternion.setFromRotationMatrix(matrix);
}

/**
 * 주행 모드의 프레임 루프. 주행 모드에서만 마운트됩니다.
 *
 * 차량 적분·차체 변환·카메라를 **한 useFrame 안에서** 순서대로 처리합니다.
 * 나눌 수 없는 이유: 카메라는 이번 프레임의 차량 위치를 따라가야 하므로 적분 뒤에
 * 실행되어야 합니다. R3F에서 실행 순서를 정하는 방법은 useFrame의 renderPriority인데,
 * 0보다 크게 주면 자동 렌더링이 꺼져서 직접 gl.render를 호출해야 합니다. 한 콜백에
 * 순서대로 두는 것이 그 부작용 없이 순서를 보장하는 방법입니다.
 *
 * 바퀴는 여기서 돌리지 않습니다. 피벗을 만든 CarModel이 직접 돌립니다 — 이유는
 * 그쪽 drivingStateRef 주석에 있습니다.
 *
 * 상태를 React가 아니라 ref에 두는 이유:
 *   60fps로 setState하면 매 프레임 컴포넌트 트리 전체가 다시 렌더됩니다. 이 화면은
 *   옵션 패널까지 함께 걸려 있어서 주행 중에 프레임을 갉아먹습니다. 화면에 숫자를
 *   띄우는 HUD도 이 ref를 자기 rAF 루프로 읽어서 자기만 다시 렌더합니다.
 */
export function CarDriver({
  vehicleId,
  bounds,
  powertrain,
  topSpeedKmh,
  zeroTo100KmhSeconds,
  kerbWeightKg,
  electricEnergy,
  regenMode,
  surface = DEFAULT_VEHICLE_SURFACE,
  course,
  assists,
  tuning,
  camera: cameraOption,
  challenge,
  interiorEye,
  sessionRef,
  challengeRef,
  telemetryRef,
  inputRef,
  stateRef,
  carGroupRef,
  bodyTiltRef,
  onLapCompleted,
  onChallengeCompleted,
}: CarDriverProps) {
  /*
   * 이 씬의 카메라는 PerspectiveCamera입니다(ShowroomCanvas가 fov를 주고 만듭니다).
   * R3F의 state.camera는 Orthographic도 담을 수 있는 넓은 타입이라 여기서 좁힙니다.
   */
  const camera = useThree((state) => state.camera as PerspectiveCamera);
  const controls = useThree((state) => state.controls);

  const spec = useMemo(
    () =>
      createVehicleSpec(
        {
          bounds,
          powertrain,
          topSpeedKmh,
          zeroTo100KmhSeconds,
          kerbWeightKg,
          electricEnergy,
        },
        tuning
      ),
    [
      bounds,
      powertrain,
      topSpeedKmh,
      zeroTo100KmhSeconds,
      kerbWeightKg,
      electricEnergy,
      tuning,
    ]
  );
  const cameraPose = useMemo(
    () => createDrivingCameraPose(cameraOption.id, bounds, cameraOption.fov, interiorEye),
    [cameraOption, bounds, interiorEye]
  );
  const terrainPose = useMemo(createTerrainPoseScratch, []);
  /** 프레임 간 유지되는 추적 시선입니다. 충돌 shake는 별도 런타임에서 합성합니다. */
  const lookAt = useRef(new Vector3()).current;
  const cameraShake = useRef(createDrivingCameraShakeState()).current;
  const followedCollisionSequenceRef = useRef(stateRef.current.collision.sequence);
  /**
   * 마지막으로 카메라가 따라간 리스폰 횟수.
   *
   * 이 값이 상태와 어긋나면 차가 순간이동했다는 뜻입니다(코스 이탈 또는 "출발선으로").
   * 위치 차이로 판단하지 않는 이유는 vehicleDriving의 spawnCount 주석에 있습니다.
   */
  const followedSpawnRef = useRef(stateRef.current.spawnCount);
  const selectedSpawnRef = useRef(course.spawn);
  selectedSpawnRef.current = course.spawn;

  useEffect(() => {
    /*
     * 주행 중에는 추격 카메라가 카메라를 단독으로 소유합니다. OrbitControls를 켜 두면
     * 같은 프레임에 update()가 카메라를 되돌려서 화면이 떱니다.
     * (CameraRig도 주행 중에는 전환을 시작하지 않습니다)
     */
    const orbit = controls as { enabled?: boolean } | null;
    if (orbit && 'enabled' in orbit) {
      orbit.enabled = false;
    }

    /*
     * 보기 모드의 카메라는 원점 근처에 있지만 이제 차량은 1,188m 떨어진 출발선에서
     * 시작합니다. damp로 따라가면 몇 초 동안 빈 맵을 가로지르므로 진입 순간 함께
     * 옮깁니다. 이후 일반 주행만 부드럽게 추적합니다.
     */
    const initialState = stateRef.current;
    const carGroup = carGroupRef.current;
    if (carGroup !== null) {
      applyTerrainPose(carGroup, initialState, terrainPose);
    }
    // 시점 변경에서는 이전 pose의 offset을 새 base 좌표에서 빼지 않도록 먼저 비웁니다.
    resetDrivingCameraShake(cameraShake);
    followedCollisionSequenceRef.current = initialState.collision.sequence;
    snapDrivingCamera(camera, lookAt, initialState, cameraPose);
  }, [
    camera,
    controls,
    cameraPose,
    stateRef,
    carGroupRef,
    terrainPose,
    lookAt,
    cameraShake,
  ]);

  /**
   * 모드를 나가면 차를 원래 상태로 되돌립니다.
   *
   * 몰던 자리에 세워 두는 편이 좋아 보이지만, 보기 모드의 카메라는 원점에서 9m 거리에
   * 원점을 보고 섭니다. 야드 어딘가에 주차된 차는 프레임에 들어오지 않아서 사용자는
   * 차가 사라졌다고 판단합니다.
   *
   * 바퀴는 여기서 되돌리지 않습니다. 상태를 0으로 초기화하면 CarModel이 다음 프레임에
   * 그 값을 그대로 써서 저절로 제자리로 돌아옵니다. 되돌리는 코드를 따로 두면 초기화와
   * 어긋날 수 있습니다.
   *
   * 정리를 여기서 하는 이유: 차량 변환을 쓴 것이 이 컴포넌트라서, 되돌리는 책임도 여기
   * 있어야 다음에 그룹 구조를 바꿀 때 한 군데만 보면 됩니다.
   */
  useEffect(
    () => () => {
      stateRef.current = createVehicleState(selectedSpawnRef.current);
      const carGroup = carGroupRef.current;
      if (carGroup !== null) {
        carGroup.position.set(0, 0, 0);
        carGroup.quaternion.identity();
      }
      bodyTiltRef.current?.rotation.set(0, 0, 0);
      bodyTiltRef.current?.position.set(0, 0, 0);
      restoreDrivingCameraBase(camera, cameraShake);
      resetDrivingCameraShake(cameraShake);
    },
    [stateRef, carGroupRef, bodyTiltRef, camera, cameraShake]
  );

  useFrame((_, delta) => {
    // delta가 튀는 경우(탭 전환 복귀)는 stepVehicle이 내부에서 잘라냅니다.
    const previous = stateRef.current;
    const input = inputRef.current;
    const next = stepVehicle(
      previous,
      input,
      delta,
      spec,
      surface,
      course.spawn,
      assists,
      regenMode,
      course.route
    );
    // 운전석 시점에서는 차체가 카메라를 가리므로 차체 모델을 숨기는 대신 시점만 안쪽에
    // 둡니다(모델 숨김은 그림자·반사까지 사라져 더 어색합니다).
    stateRef.current = next;

    /*
     * session은 성능 때문에 제자리 mutate하므로 호출 전에 완료 횟수를 숫자로 잡아 둡니다.
     * 객체 참조를 비교하면 같은 객체라 랩 완료를 영원히 놓칩니다.
     */
    const completedLapsBefore = sessionRef.current.completedLaps;
    const sessionWasActive = sessionRef.current.runActive;
    const challengeStatusBefore = challengeRef.current.status;
    const nextSession = stepDrivingSession(
      sessionRef.current,
      previous,
      next,
      input,
      delta,
      course
    );
    sessionRef.current = nextSession;
    const nextChallenge = stepDrivingChallenge(
      challengeRef.current,
      previous,
      next,
      input,
      delta,
      challenge,
      course.id
    );
    challengeRef.current = nextChallenge;
    telemetryRef.current = stepDrivingTelemetry(telemetryRef.current, {
      previousVehicle: previous,
      nextVehicle: next,
      input,
      dt: delta,
      vehicleId,
      courseId: course.id,
      courseKind: course.kind,
      sessionWasActive,
      completedLapsBefore,
      session: nextSession,
      challengeId: challenge.id,
      challengeStatusBefore,
      challenge: nextChallenge,
    });

    if (
      nextSession.completedLaps > completedLapsBefore
      && nextSession.lastTimeSeconds !== null
    ) {
      onLapCompleted?.(course.id, nextSession.lastTimeSeconds);
    }
    if (
      challengeStatusBefore !== 'complete'
      && nextChallenge.status === 'complete'
      && nextChallenge.resultValue !== null
    ) {
      onChallengeCompleted?.(nextChallenge.challengeId, nextChallenge.resultValue);
    }

    const carGroup = carGroupRef.current;
    if (carGroup !== null) {
      applyTerrainPose(carGroup, next, terrainPose);
    }

    const bodyTilt = bodyTiltRef.current;
    if (bodyTilt !== null) {
      /*
       * 이 그룹은 heading 그룹의 자식이라 로컬 축이 차량 축입니다.
       *   X = 전후축 → 롤(좌우로 기울기)
       *   Z = 좌우축 → 피치(앞이 들리고 내려감)
       * 부호는 vehicleDriving이 이미 이 축에 맞춰 계산해 둡니다.
       */
      // 가감속 기울기 + 요철 기울기. 모델에서 더하지 않고 여기서 합성합니다.
      bodyTilt.rotation.x = next.roll + next.suspensionRoll;
      bodyTilt.rotation.z = next.pitch + next.suspensionPitch;
      // 서스펜션 상하 변위. 지형 자세 그룹의 자식이라 경사에서도 노면 기준으로 움직입니다.
      bodyTilt.position.y = next.bodyHeave;
    }

    // 카메라는 이번 프레임의 차량 위치를 따라가므로 반드시 적분 뒤에 옵니다.
    if (next.spawnCount === followedSpawnRef.current) {
      // 직전 프레임 shake를 뺀 base pose만 기존 damping의 시작점으로 사용합니다.
      restoreDrivingCameraBase(camera, cameraShake);
      updateDrivingCamera(camera, lookAt, next, cameraPose, delta);
      if (next.collision.sequence !== followedCollisionSequenceRef.current) {
        followedCollisionSequenceRef.current = next.collision.sequence;
        beginDrivingCameraShake(
          cameraShake,
          next.collision.sequence,
          next.collision.strength,
          next.collision.normalX,
          next.collision.normalZ
        );
      }
      applyDrivingCameraShake(camera, lookAt, cameraPose, delta, cameraShake);
      return;
    }
    followedSpawnRef.current = next.spawnCount;
    followedCollisionSequenceRef.current = next.collision.sequence;
    resetDrivingCameraShake(cameraShake);
    snapDrivingCamera(camera, lookAt, next, cameraPose);
  }, -1);

  return null;
}
